import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Box, Collapse, Skeleton, Typography } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TimeOffRequestDialog } from "../../../pages/SynergyModeStaffing/Cells/ActiveTimeOffRequest/TimeOffRequestDialog";
import { NEUTRAL, SURFACE } from "../../../theme/tokens";
import { type TimeOffRequest, TimeOffRequestStatus } from "../../../utils/models";
import { useGetDepartmentTimeOffRequests } from "../../RequestTimeOff/timeOffRequestsHooks";
import { SystemFlatSwitch } from "../../UIToolkit/Clickable/SystemFlatSwitch";
import { TimeOffRequestCard } from "./TimeOffRequestCard";

const TimeOffRequestCardSkeleton: FC = () => (
	<Box sx={{ p: 2, mb: 1, border: `1px solid ${NEUTRAL[200]}`, borderRadius: 2, backgroundColor: SURFACE }}>
		<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
			<Skeleton variant="text" width={120} height={20} />
			<Skeleton variant="text" width={60} height={18} />
		</Box>
		<Skeleton variant="text" width="70%" height={18} />
		<Skeleton variant="text" width="40%" height={16} />
	</Box>
);

const TimeOffRequestsSkeleton: FC = () => (
	<>
		<Skeleton variant="rounded" width="100%" height={44} sx={{ mb: 1, borderRadius: 1 }} />
		<TimeOffRequestCardSkeleton />
		<TimeOffRequestCardSkeleton />
		<TimeOffRequestCardSkeleton />
		<Skeleton variant="rounded" width="100%" height={44} sx={{ mb: 1, mt: 2, borderRadius: 1 }} />
		<TimeOffRequestCardSkeleton />
		<TimeOffRequestCardSkeleton />
	</>
);

enum TimeOffRequestFilter {
	All = "all",
	Pending = "pending",
	Declined = "declined",
}

type TimeOffRequestSectionProps = {
	title: string;
	requests: TimeOffRequest[];
	isExpanded: boolean;
	onToggle: () => void;
	onCardClick: (request: TimeOffRequest) => void;
};

const TimeOffRequestSection: FC<TimeOffRequestSectionProps> = ({ title, requests, isExpanded, onToggle, onCardClick }) => {
	if (requests.length === 0) return null;

	return (
		<Box sx={{ mb: 2 }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					padding: 2,
					backgroundColor: "background.paper",
					borderRadius: 1,
					cursor: "pointer",
					mb: 1,
					"&:hover": { backgroundColor: "action.hover" },
				}}
				data-testid={`time-off-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
				onClick={onToggle}
			>
				<Typography variant="h6" sx={(theme) => ({ fontWeight: 600, color: theme.palette.primary.main })}>
					{title} ({requests.length})
				</Typography>
				{isExpanded ? (
					<ExpandLess sx={(theme) => ({ color: theme.palette.primary.main })} />
				) : (
					<ExpandMore sx={(theme) => ({ color: theme.palette.primary.main })} />
				)}
			</Box>
			<Collapse in={isExpanded}>
				<AnimatePresence mode="popLayout">
					{requests.map((request, index) => (
						<motion.div
							key={request.id}
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.3, delay: index * 0.05, ease: "easeOut" }}
						>
							<TimeOffRequestCard request={request} onClick={onCardClick} />
						</motion.div>
					))}
				</AnimatePresence>
			</Collapse>
		</Box>
	);
};

export const TimeOffRequestsTab: FC = () => {
	const { data, isLoading, refetch } = useGetDepartmentTimeOffRequests({ includeAllStatuses: true });
	const [selectedFilter, setSelectedFilter] = useState<TimeOffRequestFilter>(TimeOffRequestFilter.All);
	const [selectedStaffId, setSelectedStaffId] = useState<number | null>(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const filteredRequests = useMemo(() => {
		if (!data) return [];
		const visibleRequests = data.filter((r: TimeOffRequest) => !r.deleted && r.status !== TimeOffRequestStatus.APPROVED);
		switch (selectedFilter) {
			case TimeOffRequestFilter.Pending:
				return visibleRequests.filter((r) => r.status === TimeOffRequestStatus.PENDING);
			case TimeOffRequestFilter.Declined:
				return visibleRequests.filter((r) => r.status === TimeOffRequestStatus.DECLINED);
			default:
				return visibleRequests;
		}
	}, [data, selectedFilter]);

	const sortedRequests = useMemo(() => [...filteredRequests].sort((a, b) => a.startDate.toMillis() - b.startDate.toMillis()), [filteredRequests]);

	const groupedRequests = useMemo(() => {
		const now = DateTime.now();
		const currentWeekStart = now.startOf("week");
		const nextWeekStart = currentWeekStart.plus({ weeks: 1 });

		const currentWeek: TimeOffRequest[] = [];
		const nextWeek: TimeOffRequest[] = [];
		const future: TimeOffRequest[] = [];

		for (const request of sortedRequests) {
			const requestWeekStart = request.startDate.startOf("week");

			if (requestWeekStart <= currentWeekStart) {
				currentWeek.push(request);
			} else if (requestWeekStart.equals(nextWeekStart)) {
				nextWeek.push(request);
			} else {
				future.push(request);
			}
		}

		return { currentWeek, nextWeek, future };
	}, [sortedRequests]);

	const [expandedSections, setExpandedSections] = useState({
		currentWeek: true,
		nextWeek: true,
		future: false,
	});

	// Expand "future" by default the first time data loads if there are no requests in current/next week.
	// Ref guard keeps this a one-shot so user toggles and refetches don't re-open it.
	const hasSetFutureDefault = useRef(false);
	useEffect(() => {
		if (isLoading || hasSetFutureDefault.current) return;
		hasSetFutureDefault.current = true;
		if (!groupedRequests.currentWeek.length && !groupedRequests.nextWeek.length) {
			setExpandedSections((prev) => ({ ...prev, future: true }));
		}
	}, [isLoading, groupedRequests.currentWeek.length, groupedRequests.nextWeek.length]);

	const toggleSection = (section: keyof typeof expandedSections): void => {
		setExpandedSections((prev) => ({
			...prev,
			[section]: !prev[section],
		}));
	};

	const handleCardClick = useCallback((request: TimeOffRequest) => {
		setSelectedStaffId(request.requestingStaffId);
		setDialogOpen(true);
	}, []);

	const handleDialogClose = useCallback(() => {
		setDialogOpen(false);
		setSelectedStaffId(null);
	}, []);

	const staffTimeOffRequests = useMemo(() => {
		if (!data || selectedStaffId === null) return [];
		return data.filter((r) => r.requestingStaffId === selectedStaffId && !r.deleted);
	}, [data, selectedStaffId]);

	const handleRefetch = useCallback(async () => {
		await refetch();
	}, [refetch]);

	return (
		<Box data-testid="time-off-requests-tab-root" sx={{ overflow: "hidden", height: "100%" }}>
			<Box sx={{ display: "flex", justifyContent: "center" }}>
				<SystemFlatSwitch
					fontSizeOverrides={{
						xs: "12px",
						xl: "12px",
						xxl: "12px",
					}}
					maxWidthOverrides={{
						xs: "80px",
						xl: "86px",
						xxl: "86px",
					}}
					options={[
						{ value: TimeOffRequestFilter.All, label: "All" },
						{ value: TimeOffRequestFilter.Pending, label: "Pending" },
						{ value: TimeOffRequestFilter.Declined, label: "Declined" },
					]}
					activeIndexOrValue={selectedFilter}
					onSwitch={(option) => setSelectedFilter(option.value)}
					data-testid="time-off-requests-filter"
				/>
			</Box>
			<Box sx={{ overflowX: "hidden", height: "calc(100% - 50px)", marginTop: 1 }}>
				{isLoading ? (
					<TimeOffRequestsSkeleton />
				) : sortedRequests.length === 0 ? (
					<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100px" }}>
						<Typography color="textSecondary">No time off requests</Typography>
					</Box>
				) : (
					<>
						<TimeOffRequestSection
							title="Current Week"
							requests={groupedRequests.currentWeek}
							isExpanded={expandedSections.currentWeek}
							onToggle={() => toggleSection("currentWeek")}
							onCardClick={handleCardClick}
						/>
						<TimeOffRequestSection
							title="Next Week"
							requests={groupedRequests.nextWeek}
							isExpanded={expandedSections.nextWeek}
							onToggle={() => toggleSection("nextWeek")}
							onCardClick={handleCardClick}
						/>
						<TimeOffRequestSection
							title="Future"
							requests={groupedRequests.future}
							isExpanded={expandedSections.future}
							onToggle={() => toggleSection("future")}
							onCardClick={handleCardClick}
						/>
					</>
				)}
			</Box>

			{dialogOpen && selectedStaffId !== null && (
				<TimeOffRequestDialog
					dialogOpen={dialogOpen}
					handleClose={handleDialogClose}
					timeOffRequestsOfSpecificStaff={staffTimeOffRequests}
					refetchRequests={handleRefetch}
				/>
			)}
		</Box>
	);
};
